import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { getIndexBySlug } from "@/utils/indexIdentity";
import useRealtimeData from "@/hooks/useRealtimeData";
import StockLogo from "@/components/StockLogo";
import { Button } from "@/components/ui/button";
import { CreateAlertModal } from "@/pages/stock-detail/CreateAlertModal";
import { openTerminal as routerOpenTerminal } from "@/utils/terminalRouter";
import { toTradingViewSymbol } from "@/utils/tradingViewMapping";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, ExternalLink, Link2, Bell, Bookmark, BookmarkCheck,
  Activity, Loader2, ChevronLeft, ChevronRight, ChevronRight as ChevronRightIcon
} from "lucide-react";
import { useCriticalLogos } from "@/hooks/useCriticalLogos";

import IndexChart from "./IndexChart";
import PerformancePanel from "./PerformancePanel";
import FnOTab from "./FnOTab";
import ETFsTab from "./ETFsTab";
import AllIndicesTable from "./AllIndicesTable";
import TopOptionsSidebar from "./TopOptionsSidebar";
import { fmt, fmtCr, INDEX_LOGOS } from "./utils";
import { isPeriodReady } from "@/components/charts/chartState";

export default function IndexDetailPage() {
  const { indexSlug: slug } = useParams();
  const navigate = useNavigate();
  const [indexMeta, setIndexMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [chartType, setChartType] = useState("line");
  const [tf, setTf] = useState("1D");
  const [periodChange, setPeriodChange] = useState(null); // {periodChange, periodChangePercent}

  // Data states
  const [constituents, setConstituents] = useState([]);
  const [noConstituentData, setNoConstituentData] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [consWsSymbols, setConsWsSymbols] = useState([]);
  const [futures, setFutures] = useState([]);
  const [futureWsSymbols, setFutureWsSymbols] = useState([]);
  const [options, setOptions] = useState([]);
  const [optionWsSymbols, setOptionWsSymbols] = useState([]);
  const [oiSummary, setOiSummary] = useState({});
  const [etfs, setEtfs] = useState([]);
  const [etfWsSymbols, setEtfWsSymbols] = useState([]);

  // BFF: single call hydrates all sections (eliminates waterfall)
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/indices/${slug}/bff`, { params: { page, per_page: 10 } })
      .then(r => {
        const d = r.data;
        setIndexMeta(d.detail);
        // Constituents
        setConstituents(d.constituents?.constituents || []);
        setTotalPages(d.constituents?.total_pages || 1);
        setConsWsSymbols(d.constituents?.ws_symbols || []);
        setNoConstituentData(d.constituents?.no_constituent_data || false);
        // Futures
        setFutures(d.futures?.futures || []);
        setFutureWsSymbols(d.futures?.ws_symbols || []);
        // Top Options
        setOptions(d.top_options?.options || []);
        setOptionWsSymbols(d.top_options?.ws_symbols || []);
        setOiSummary(d.top_options?.oi_summary || {});
        // ETFs
        setEtfs(d.etfs?.etfs || []);
        setEtfWsSymbols(d.etfs?.ws_symbols || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug, page]);

  // WebSocket subscriptions
  const exchange = indexMeta?.exchange || "NSE";
  const wsSymbol = indexMeta?.ws_symbol || "";
  const { ticks: indexTicks } = useRealtimeData(wsSymbol ? [wsSymbol] : [], exchange);

  // Batch-prefetch logos for all constituent symbols
  const constituentSymbols = useMemo(() => constituents.map(c => c.symbol).filter(Boolean), [constituents]);
  useCriticalLogos(constituentSymbols, { critical: true });
  const liveTick = indexTicks[wsSymbol] || {};
  const { ticks: consTicks } = useRealtimeData(consWsSymbols, "NSE");
  const { ticks: futureTicks } = useRealtimeData(futureWsSymbols, indexMeta?.fno_exchanges?.[0] || "NFO");
  const { ticks: optionTicks } = useRealtimeData(optionWsSymbols, indexMeta?.fno_exchanges?.[0] || "NFO");
  const { ticks: etfTicks } = useRealtimeData(etfWsSymbols, "NSE");

  // Computed values
  const liveOhlc = useMemo(() => {
    const ohlc = liveTick?.ohlc || indexMeta?.ohlc || {};
    return { ...ohlc, lastPrice: liveTick?.last_price || indexMeta?.last_price || 0 };
  }, [liveTick, indexMeta]);

  const price = liveTick?.last_price || indexMeta?.last_price || 0;
  const rawChange = liveTick?.change !== undefined ? liveTick.change : indexMeta?.change || 0;
  const rawChangePct = liveTick?.change_percent !== undefined ? liveTick.change_percent : indexMeta?.change_percent || 0;

  // Use period-based change when timeframe != 1D AND data is ready, else honest states
  const isNon1D = tf !== "1D";
  const hasPeriodData = isNon1D && isPeriodReady(periodChange);
  const change = hasPeriodData ? periodChange.periodChange : (isNon1D ? null : rawChange);
  const changePct = hasPeriodData ? periodChange.periodChangePercent : (isNon1D ? null : rawChangePct);
  const changeIsNull = change === null || change === undefined;
  const isPositive = !changeIsNull && changePct >= 0;

  // Reset period change on ALL timeframe switches (fixes stale tf→tf carry-over)
  const handlePeriodChange = useCallback((data) => {
    setPeriodChange(data);
  }, []);

  useEffect(() => {
    setPeriodChange(null);
  }, [tf]);

  // Display-friendly name mapping — use full name as canonical source of truth
  const displayName = indexMeta?.name || indexMeta?.short || "";
  // Short name for option/futures naming (e.g., "NIFTY" from fno_name, or "NIFTY 50")
  const indexShortName = indexMeta?.fno_name || indexMeta?.short || displayName;

  // Alert modal state
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  // Watchlist state
  const [inWatchlist, setInWatchlist] = useState(false);

  const handleOpenTerminal = () => {
    routerOpenTerminal(wsSymbol, { exchange: exchange || 'NSE' });
  };

  // Compute TradingView symbol for the Terminal link
  const tvSymbol = useMemo(() => {
    return toTradingViewSymbol(wsSymbol);
  }, [wsSymbol]);

  const handleToggleWatchlist = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { toast.error("Please login to add to watchlist"); return; }
    try {
      const res = await axios.post(`${API}/watchlist/toggle`, {
        symbol: wsSymbol,
        name: displayName,
        exchange: exchange || 'NSE',
        segment: 'index',
        asset_class: 'index',
      }, { headers: { Authorization: `Bearer ${token}` } });
      setInWatchlist(!!res.data.in_watchlist);
      toast.success(res.data.in_watchlist ? `${displayName} added to watchlist` : `${displayName} removed from watchlist`);
    } catch {
      toast.error("Failed to update watchlist");
    }
  }, [wsSymbol, displayName, exchange]);

  if (loading || !indexMeta) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview" },
    ...(indexMeta.has_fno ? [{ id: "fno", label: "F&O" }] : []),
    { id: "etfs", label: "ETFs" },
  ];

  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-4" data-testid="index-detail-page">
      {/* Header Row: Logo + Name + Price */}
      <div className="mb-2">
        <div className="flex items-center gap-3 mb-1">
          {INDEX_LOGOS[slug] && (
            <div className="w-10 h-10 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0" data-testid="index-logo">
              <img src={INDEX_LOGOS[slug]} alt={displayName} className="w-7 h-7 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900" data-testid="index-name">{displayName}</h1>
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-medium">{indexMeta.exchange}</span>
            </div>
          </div>
        </div>
        <div className="flex items-baseline gap-3">
          <span className="text-3xl font-bold text-gray-900" data-testid="index-price">{price ? fmt(price) : "--"}</span>
          {changeIsNull ? (
            <span className="text-lg font-semibold text-gray-400" data-testid="index-change" title="Data temporarily unavailable">{'\u2014'}</span>
          ) : (
            <span className={`text-lg font-semibold ${isPositive ? "text-[#00b386]" : "text-[#eb5b3c]"}`} data-testid="index-change">
              {change >= 0 ? "+" : ""}{change.toFixed(2)} ({change >= 0 ? "+" : "-"}{Math.abs(changePct).toFixed(2)}%)
            </span>
          )}
          {tf !== "1D" && <span className="text-xs text-gray-400 ml-1" data-testid="tf-label">{tf}</span>}
        </div>
      </div>

      {/* Layout: Main + Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-0">
          {/* Action buttons — above chart, top-right of chart area */}
          <div className="flex items-center justify-end gap-3 mb-2" data-testid="chart-action-buttons">
            <button
              onClick={() => setAlertModalOpen(true)}
              className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:border-gray-300 transition-all"
              data-testid="create-alert-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="12" cy="12" r="9"/>
                <path d="M12 6v6l4 2"/>
                <path d="M5.5 5.5L3 3"/>
                <path d="M18.5 5.5L21 3"/>
              </svg>
              Create Alert
            </button>
            <button
              onClick={handleToggleWatchlist}
              className={`flex items-center gap-2 px-3 py-2 bg-gray-50 border rounded-lg text-sm font-medium transition-all ${
                inWatchlist
                  ? "border-yellow-400 text-yellow-600 hover:bg-yellow-50"
                  : "border-gray-200 text-gray-600 hover:bg-gray-100 hover:border-gray-300"
              }`}
              data-testid="watchlist-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill={inWatchlist ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
              </svg>
              {inWatchlist ? "Watchlisted" : "Watchlist"}
            </button>
            {indexMeta.has_fno && indexMeta.option_chain_symbol && (
              <Link
                to={`/option-chain?symbol=${indexMeta.option_chain_symbol}`}
                className="flex items-center gap-1.5 text-sm font-medium text-[#00b386] hover:text-[#00a076] transition-colors"
                data-testid="oc-link"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
                Option Chain
              </Link>
            )}
          </div>

          {/* Chart (dominant section) */}
          <IndexChart
            symbol={wsSymbol}
            exchange={exchange}
            liveTick={liveTick}
            chartType={chartType}
            setChartType={setChartType}
            tf={tf}
            setTf={setTf}
            onPeriodChange={handlePeriodChange}
            onOpenTerminal={handleOpenTerminal}
            tvSymbol={tvSymbol}
          />

          {/* Tabs — below chart (like Groww) */}
          <div className="flex gap-0 border-b border-gray-200 mt-4 mb-6">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-5 py-3 text-sm font-bold transition-colors relative ${
                  tab === t.id
                    ? "text-[#00b386] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-[#00b386]"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                data-testid={`tab-${t.id}`}
              >{t.label}</button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="space-y-8">
          {tab === "overview" && (
            <div className="space-y-8">
              {/* Performance */}
              <PerformancePanel liveOhlc={liveOhlc} week52={indexMeta.week_52} />

              {/* Constituents Table */}
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-4">{displayName} Companies</h2>
                {noConstituentData ? (
                  <div className="text-center py-10 border border-dashed border-gray-200 rounded-lg" data-testid="no-constituents">
                    <p className="text-sm text-gray-500">Constituent data is not available for this index</p>
                    <p className="text-xs text-gray-400 mt-1">Constituent information may not be tracked for all indices</p>
                  </div>
                ) : constituents.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="animate-spin w-5 h-5 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-500">Loading constituents...</span>
                  </div>
                ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3">Company</th>
                        <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3">Market Cap</th>
                        <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3">Market Price</th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3">Sector</th>
                      </tr>
                    </thead>
                    <tbody>
                      {constituents.map((c) => {
                        const tick = consTicks[c.symbol] || {};
                        const cPrice = tick.last_price || c.last_price || 0;
                        const chgPct = tick.change_percent || c.change_percent || 0;
                        const chgAbs = tick.change || c.change || 0;
                        const isPos = chgPct >= 0;
                        return (
                          <tr
                            key={c.symbol}
                            className="border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => navigate(`/stock/${c.symbol}`)}
                            data-testid={`constituent-${c.symbol}`}
                          >
                            <td className="py-3 px-3">
                              <div className="flex items-center gap-3">
                                <StockLogo symbol={c.symbol} name={c.name} size="sm" />
                                <div>
                                  <p className="text-sm font-semibold text-gray-900 leading-tight">{c.name}</p>
                                  <p className="text-xs text-gray-400">{c.symbol}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-3 text-right text-sm text-gray-700">
                              {c.market_cap ? fmtCr(c.market_cap) : "N/A"}
                            </td>
                            <td className="py-3 px-3 text-right">
                              <p className="text-sm font-semibold text-gray-900">{cPrice ? `₹${fmt(cPrice)}` : "--"}</p>
                              <p className={`text-xs ${isPos ? "text-[#00b386]" : "text-[#eb5b3c]"}`}>
                                {chgAbs ? `${chgAbs.toFixed(2)} ` : ""}
                                ({chgPct.toFixed(2)}%)
                              </p>
                            </td>
                            <td className="py-3 px-3 text-sm text-gray-500">{c.sector || "--"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                )}
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-4">
                    <button
                      disabled={page <= 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    ><ChevronLeft className="w-4 h-4" /></button>
                    <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
                    <button
                      disabled={page >= totalPages}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    ><ChevronRight className="w-4 h-4" /></button>
                  </div>
                )}
              </div>

              {/* All Indices */}
              <AllIndicesTable navigate={navigate} />
            </div>
          )}

          {tab === "fno" && (
            <FnOTab
              indexName={displayName}
              indexShortName={indexShortName}
              optionChainSymbol={indexMeta.option_chain_symbol}
              futures={futures}
              options={options}
              oiSummary={oiSummary}
              futureTicks={futureTicks}
              optionTicks={optionTicks}
              kiteAuthenticated={indexMeta.broker_authenticated !== false}
              navigate={navigate}
            />
          )}

          {tab === "etfs" && (
            <ETFsTab etfs={etfs} etfTicks={etfTicks} navigate={navigate} />
          )}
          </div>
        </div>

        {/* Sidebar */}
        {indexMeta.has_fno && (
          <div>
            <TopOptionsSidebar
              indexName={displayName}
              indexShortName={indexShortName}
              options={options}
              optionTicks={optionTicks}
              oiSummary={oiSummary}
              kiteAuthenticated={indexMeta.broker_authenticated !== false}
              navigate={navigate}
            />
          </div>
        )}
      </div>

      {/* Create Alert Modal */}
      <CreateAlertModal
        stock={{ symbol: wsSymbol, name: displayName, price: price }}
        isOpen={alertModalOpen}
        onClose={() => setAlertModalOpen(false)}
      />
    </div>
  );
}
